import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalController, PopoverController } from '@ionic/angular';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import {
  CUSTOM_FIELD_TYPE_SHORT_ANSWER,
  CUSTOM_FIELD_TYPE_LONG_ANSWER,
  CUSTOM_FIELD_TYPE_CHECKBOX,
  CUSTOM_FIELD_TYPE_USER_ATTRIBUTE,
  CUSTOM_FIELD_TYPE_SINGLE_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_SELECT,
  CUSTOM_FIELD_TYPE_USER_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
  CUSTOM_FIELD_TYPE_DATE,
  CUSTOM_FIELD_TYPE_TEXT,
  CUSTOM_FIELD_TYPE_FILE_UPLOAD,
  CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
} from 'src/app/constants';
import { flattenAdditionalIdpFields, parseExternalSelectFunctionName } from 'src/app/utils/utils';
import { CustomField, GlobalCustomField } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { DatetimeModalComponent } from '../datetime-modal/datetime-modal.component';
import { EditCustomFieldComponent } from '../edit-custom-field/edit-custom-field.component';

@Component({
  selector: 'app-custom-fields',
  templateUrl: './custom-fields.component.html',
  styleUrls: ['./custom-fields.component.scss'],
})
export class CustomFieldsComponent implements OnInit {
  @Input() customFields: any[] = [];
  @Input() addCustomFieldButtonLabel: string;
  @Input() placeholder: string;
  @Input() showPublicOption: boolean;
  @Input() showUserAttributeOption: boolean; // This isn't currently used anywhere
  @Input() showBranchingInput: boolean;
  @Input() showRequiredOption: boolean = true;
  @Input() showDescription: boolean;
  @Input() contextPrefix: string;
  @Input() readOnly: boolean;
  @Input() hideAddAndDeleteButtons: boolean;
  @Input() editMode: boolean;
  @Input() projectId: string;

  dropdownOptionFields: string[] = [];
  allowedValueFields: string[] = [];
  customFieldTypes = [
    {
      displayName: 'Short answer',
      key: CUSTOM_FIELD_TYPE_SHORT_ANSWER,
    },
    {
      displayName: 'Long answer',
      key: CUSTOM_FIELD_TYPE_LONG_ANSWER,
    },
    {
      displayName: 'Checkbox',
      key: CUSTOM_FIELD_TYPE_CHECKBOX,
    },
    {
      displayName: 'Single select',
      key: CUSTOM_FIELD_TYPE_SINGLE_SELECT,
    },
    {
      displayName: 'Multi select',
      key: CUSTOM_FIELD_TYPE_MULTI_SELECT,
    },
    {
      displayName: 'User select',
      key: CUSTOM_FIELD_TYPE_USER_SELECT,
    },
    {
      displayName: 'Multi user select',
      key: CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
    },
    {
      displayName: 'User group select',
      key: CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
    },
    {
      displayName: 'Multi user group select',
      key: CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
    },
    {
      displayName: 'Date',
      key: CUSTOM_FIELD_TYPE_DATE,
    },
    {
      displayName: 'Text',
      key: CUSTOM_FIELD_TYPE_TEXT,
    },
    {
      displayName: 'File upload',
      key: CUSTOM_FIELD_TYPE_FILE_UPLOAD,
    },
  ];

  dynamicFieldLabels: Record<string, Record<string, string>> = {};
  allowedValueLabels: Record<string, string> = {};
  userAttributes: any = [];

  projectLevelCustomFields: GlobalCustomField[] = [];

  constructor(
    private authService: AuthService,
    private popoverCtrl: PopoverController,
    private searchablePopoverService: SearchablePopoverService,
    private apiService: ApiService,
    private modalCtrl: ModalController,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
  ) {
    if (!this.projectId) {
      this.projectId = this.route.snapshot.params['projectId'];
    }
  }

  async ngOnInit() {
    if (this.showUserAttributeOption) {
      this.customFieldTypes.push({
        displayName: 'External IDP attribute',
        key: CUSTOM_FIELD_TYPE_USER_ATTRIBUTE,
      });

      if (this.authService.profile.additionalIdpFields) {
        this.userAttributes = Object.keys(flattenAdditionalIdpFields(this.authService.profile.additionalIdpFields));
      }
    }

    await this.loadProjectLevelCustomFields();
    await this.loadDynamicFieldLabels();
  }

  async showAddCustomFieldPopover(event: any) {
    const existingFieldIds = this.customFields.map((f) => f.id);

    this.searchablePopoverService.customFields({
      event,
      projectId: this.projectId,
      excludeIds: existingFieldIds,
      dynamicCreationCallback: (searchText: string) => {
        this.showCreateNewFieldModal(searchText);
      },
      dynamicCreationCallbackLabel: 'Create new field',
      callback: (_id: string, _name: string, selectedField: CustomField) => {
        const newFieldToAdd: CustomField = {
          ...selectedField,
          // Old version of custom fields uses name as label, new version uses label
          label: (selectedField as any).name || selectedField.label,
          public: true,
          required: false,
          conditions: {
            type: 'simple',
            simple: {},
          },
        };

        if (!this.customFields) {
          this.customFields = [];
        }

        this.customFields.push(newFieldToAdd);

        this.loadDynamicFieldLabels();
      },
    });
  }

  async loadProjectLevelCustomFields() {
    if (this.projectId) {
      const project = await this.projectsService.getById(this.projectId);
      this.projectLevelCustomFields = project.fields || [];
    }
  }

  async showCreateNewFieldModal(text: string) {
    const modal = await this.modalCtrl.create({
      component: EditCustomFieldComponent,
      cssClass: 'modal-large',
      componentProps: {
        text,
        projectId: this.projectId,
      },
    });

    await modal.present();

    modal.onDidDismiss().then((data) => {
      if (data?.data?.customField) {
        const newFieldToAdd: CustomField = {
          ...data.data.customField,
          // Old version of custom fields uses name as label, new version uses label
          public: true,
          required: false,
          conditions: {
            type: 'simple',
            simple: {},
          },
        };

        if (!this.customFields) {
          this.customFields = [];
        }

        this.customFields.push(newFieldToAdd);

        // Reload labels to potentially fetch for the new field's allowedValues
        this.loadDynamicFieldLabels();
      }
    });
  }

  showAllowedValueConfig(field: any) {
    return [
      CUSTOM_FIELD_TYPE_USER_SELECT,
      CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
      CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
    ].includes(field.type);
  }

  addCustomField() {
    if (!this.customFields) {
      this.customFields = [];
    }

    // Cool random arbitrary limit on length
    if (this.customFields.length >= 100) {
      return;
    }

    this.customFields.push({
      label: '',
      description: '',
      // value: '',
      type: CUSTOM_FIELD_TYPE_SHORT_ANSWER,
      required: false,
      public: true,
      id: uuidv4(),
      conditions: {
        type: 'simple',
        simple: {},
      },
    });
  }

  async reorderField(index: number) {
    const MOVE_UP = 'Move up';
    const MOVE_DOWN = 'Move down';

    const options: ActionDropdownType[] = [];

    if (index !== 0) {
      options.push({
        label: MOVE_UP,
        icon: 'arrow-up-outline',
      });
    }

    if (index < this.customFields.length - 1) {
      options.push({
        label: MOVE_DOWN,
        icon: 'arrow-down-outline',
      });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_index: number, label: string) => {
          popover.dismiss();

          const item = this.customFields.splice(index, 1)[0];
          const newIndex = label === MOVE_UP ? index - 1 : index + 1;
          this.customFields.splice(newIndex, 0, item);
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  deleteCustomField(index: number) {
    this.customFields.splice(index, 1);
  }

  async addAllowedValue(fieldIndex: number, label: string) {
    const popoverServiceMethod = {
      [CUSTOM_FIELD_TYPE_USER_SELECT]: 'users',
      [CUSTOM_FIELD_TYPE_MULTI_USER_SELECT]: 'users',
      [CUSTOM_FIELD_TYPE_USER_GROUP_SELECT]: 'userGroups',
      [CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT]: 'userGroups',
    };
    const type = this.customFields[fieldIndex].type;

    this.searchablePopoverService[popoverServiceMethod[type]]({
      event,
      projectId: this.projectId,
      callback: (id: string, label: string, selectedItem: any) => {
        if (!this.customFields[fieldIndex].allowedValues) {
          this.customFields[fieldIndex].allowedValues = [id];
          this.allowedValueLabels[id] = label;
        } else {
          this.customFields[fieldIndex].allowedValues.push(id);
          this.allowedValueLabels[id] = label;
        }
      },
    });
  }

  deleteAllowedValue(fieldIndex: number, optionIndex: number) {
    this.customFields[fieldIndex].allowedValues.splice(optionIndex, 1);
  }

  async showCustomFieldOptions(index: number) {
    const COPY_ID = 'Copy input field ID';
    const EDIT = 'Edit';
    const DELETE = 'Delete';

    const options: ActionDropdownType[] = [];

    const customField = this.customFields[index];

    if (customField.id) {
      options.push({
        label: COPY_ID,
        icon: 'copy-outline',
      });
    }

    if (!this.hideAddAndDeleteButtons) {
      options.push({
        label: EDIT,
        icon: 'pencil-outline',
      });
      options.push({
        label: DELETE,
        icon: 'trash-outline',
        destructive: true,
      });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_index: number, label: string) => {
          popover.dismiss();

          if (label === COPY_ID) {
            navigator.clipboard
              .writeText(customField.id)
              .then(() => {})
              .catch(() => {
                // Unable to copy
              });
          } else if (label === DELETE) {
            this.deleteCustomField(index);
          } else if (label === EDIT) {
            this.showEditCustomFieldModal(index);
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async showEditCustomFieldModal(index: number) {
    const modal = await this.modalCtrl.create({
      component: EditCustomFieldComponent,
      cssClass: 'modal-large',
      componentProps: {
        id: this.customFields[index].id,
        projectId: this.projectId,
      },
    });

    await modal.present();

    modal.onDidDismiss().then((data) => {
      if (data?.data?.customField) {
        this.customFields[index] = data.data.customField;
      }
    });
  }

  addCustomFieldOption(fieldIndex: number, label: string) {
    if (!label) {
      return;
    }

    if (!this.customFields[fieldIndex].options) {
      this.customFields[fieldIndex].options = [label];
    } else {
      this.customFields[fieldIndex].options.push(label);
    }

    this.dropdownOptionFields = [];
  }

  deleteCustomFieldOption(fieldIndex: number, optionIndex: number) {
    this.customFields[fieldIndex].options.splice(optionIndex, 1);
  }

  toggleConditionsShown(fieldIndex: number) {
    const field = this.customFields[fieldIndex];

    if (this.getConditionsForField(field)?.length) {
      field.conditions.simple[this.getConditionsType(field)] = [];
    } else {
      this.addSimpleCondition(fieldIndex);
    }
  }

  addSimpleCondition(fieldIndex: number) {
    const field = this.customFields[fieldIndex];

    if (!this.getConditionsForField(field)?.length) {
      field.conditions = {
        type: 'simple',
        simple: {
          and: [],
        },
      };
    }

    field.conditions.simple[this.getConditionsType(field)] = [
      ...this.getConditionsForField(field),
      {
        leftValueFrom: '',
        operator: 'equals',
        rightValue: null,
      },
    ];
  }

  removeSimpleCondition(fieldIndex: number, conditionIndex: number) {
    const field = this.customFields[fieldIndex];

    field.conditions.simple[this.getConditionsType(field)].splice(conditionIndex, 1);
  }

  onSimpleConditionFieldChange(event: any, condition: any) {
    // Reset the value when the field changes
    condition.rightValue = null;
  }

  getFieldType(fieldLabel: string, i: number): string {
    const field = this.getConditionalFieldOptions(i).find((f) => this.getFieldIdPath(f.id) === fieldLabel);

    return field ? field.type : 'text';
  }

  async showExternalSelectOptions(fieldLabel: string, condition: any, i: number) {
    const field = this.getConditionalFieldOptions(i).find((f) => this.getFieldIdPath(f.id) === fieldLabel);
    const isMultiSelect = [CUSTOM_FIELD_TYPE_MULTI_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_SELECT].includes(field.type);
    const onSaveCallback = (id: string, label: string) => {
      let value: string | string[] | null = null;

      if (isMultiSelect) {
        value = Array.from(new Set<string>(condition.rightValue ?? []).add(id));
      } else {
        if (id !== condition.rightValue) {
          value = id;
        }
      }

      condition.rightValue = value;

      if (field.externalSelect != null) {
        const valueArr = Array.isArray(value) ? value : [value];

        for (const v of valueArr) {
          if (v === id) {
            this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] = {
              ...(this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] ?? {}),
              [`${field.externalSelect}:${v}`]: label,
            };
          }
        }
      }
    };

    this.searchablePopoverService.customFieldOptions({
      event,
      field,
      context: {},
      callback: onSaveCallback,
    });
  }

  compareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  getConditionalFieldOptions(i?: number) {
    const currentFieldId = i != null ? this.customFields[i]?.id : null;

    return [
      ...(this.projectLevelCustomFields || []),
      ...(i != null ? this.customFields.slice(0, i) : this.customFields),
    ].filter(
      (f, j) =>
        f.id !== currentFieldId && // Don't show the current field in the list of options
        !new Set([
          CUSTOM_FIELD_TYPE_USER_SELECT,
          CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
          CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
          CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
          CUSTOM_FIELD_TYPE_TEXT,
          CUSTOM_FIELD_TYPE_SHORT_ANSWER,
          CUSTOM_FIELD_TYPE_LONG_ANSWER,
        ]).has(f.type),
    );
  }

  getFieldIdPath(id: string) {
    return `${this.contextPrefix}['${id}']`;
  }

  getConditionsForField(field: any) {
    return field.conditions?.simple?.[this.getConditionsType(field)] ?? [];
  }

  toggleConditionsType(field: any) {
    if (field.conditions?.simple?.and?.length) {
      field.conditions.simple.or = field.conditions.simple.and;
      field.conditions.simple.and = null;
    } else if (field.conditions?.simple?.or?.length) {
      field.conditions.simple.and = field.conditions.simple.or;
      field.conditions.simple.or = null;
    }
  }

  getConditionsType(field: any) {
    return field.conditions?.simple?.and ? 'and' : 'or';
  }

  onReorder({ detail }, item: any) {
    const { from, to } = detail;

    item.options.splice(to, 0, item.options.splice(from, 1)[0]);

    detail.complete();
  }

  trackValueBy(_i: number, value: { label: string; value: string }) {
    return value.value;
  }

  getExternalSelectFieldLabel(fieldLabel: string, value: string, i: number) {
    const field = this.getConditionalFieldOptions(i).find((f) => this.getFieldIdPath(f.id) === fieldLabel);

    if (field.externalSelect != null) {
      return this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT]?.[`${field.externalSelect}:${value}`] ?? value;
    }

    return value;
  }

  getAllowedValueLabel(value: string): string {
    return this.allowedValueLabels?.[value] ?? value;
  }

  /**
   * Fetches human-readable labels for IDs stored in conditions involving
   * user select, user group select, and external select custom fields.
   * Also fetches labels for IDs stored in the 'allowedValues' property of
   * user select and user group select custom fields.
   * This is necessary because conditions and allowedValues store IDs,
   * but the UI needs to display friendly names.
   */
  async loadDynamicFieldLabels() {
    // Flatten conditions from all relevant custom fields.
    // Assumes getConditionsForField retrieves filter/condition data associated with a field.
    const allConditions = this.customFields?.flatMap((f) => this.getConditionsForField(f)) ?? [];

    // Use Sets to collect unique IDs/values requiring label lookups.
    const userSelectFields = new Set<string>();
    const userGroupSelectFields = new Set<string>();
    // For external selects, store a composite key: \'functionName:value\'
    const externalSelectFields = new Set<string>();

    // Process conditions to gather IDs
    for (const condition of allConditions) {
      const field = this.getConditionalFieldOptions().find(
        (f) => this.getFieldIdPath(f.id) === condition.leftValueFrom,
      );

      // Ensure the condition value(s) are in an array format and filter out falsy values.
      const valueArr = Array.isArray(condition.rightValue)
        ? condition.rightValue.filter(Boolean)
        : [condition.rightValue].filter(Boolean);

      // Add IDs/values to the appropriate set based on the custom field type.
      if ([CUSTOM_FIELD_TYPE_USER_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_SELECT].includes(field.type)) {
        for (const value of valueArr) userSelectFields.add(value);
      } else if (
        [CUSTOM_FIELD_TYPE_USER_GROUP_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT].includes(field.type)
      ) {
        for (const value of valueArr) userGroupSelectFields.add(value);
      } else if (
        [CUSTOM_FIELD_TYPE_SINGLE_SELECT, CUSTOM_FIELD_TYPE_MULTI_SELECT].includes(field.type) &&
        field.externalSelect != null
      ) {
        // External select values need the function name to be looked up correctly.
        for (const value of valueArr) externalSelectFields.add(`${field.externalSelect}:${value}`);
      }
    }

    // Process allowedValues to gather IDs
    for (const field of this.customFields) {
      if (field.allowedValues?.length) {
        if ([CUSTOM_FIELD_TYPE_USER_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_SELECT].includes(field.type)) {
          for (const id of field.allowedValues) userSelectFields.add(id);
        } else if (
          [CUSTOM_FIELD_TYPE_USER_GROUP_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT].includes(field.type)
        ) {
          for (const id of field.allowedValues) userGroupSelectFields.add(id);
        }
      }
    }

    // Only make an API call if there are IDs/values to look up.
    if (userSelectFields.size || externalSelectFields.size || userGroupSelectFields.size) {
      // Use the bulk lookup endpoint to fetch labels efficiently.
      // It expects IDs grouped by type (assigneeId for users, userGroupId for groups, externalSelectValue for external).
      const lookupValues: any = await this.apiService.postPromise('/saved-filters/get-lookup-values', {
        assigneeId: Array.from(userSelectFields),
        externalSelectValue: Array.from(externalSelectFields),
        userGroupId: Array.from(userGroupSelectFields),
      });

      // Store labels for conditions
      this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_USER_SELECT] = lookupValues.assigneeId ?? {};
      this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] = lookupValues.externalSelectValue ?? {};
      this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_USER_GROUP_SELECT] = lookupValues.userGroupId ?? {};

      // Initialize or merge labels for allowedValues into allowedValueLabels
      this.allowedValueLabels = {
        ...(this.allowedValueLabels ?? {}), // Keep labels added via addAllowedValue
        ...(lookupValues.assigneeId ?? {}), // Add/overwrite user labels
        ...(lookupValues.userGroupId ?? {}), // Add/overwrite group labels
      };
    } else {
      this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_USER_SELECT] = {};
      this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] = {};
      this.dynamicFieldLabels[CUSTOM_FIELD_TYPE_USER_GROUP_SELECT] = {};
      this.allowedValueLabels = { ...(this.allowedValueLabels ?? {}) }; // Keep manually added ones if desired, or set to {}
    }
  }

  removeMultiSelectOptionFromCondition(condition: any, value: string) {
    condition.rightValue = condition.rightValue.filter((o) => o !== value);
  }

  isExternalSelectField(field: any) {
    return (
      [CUSTOM_FIELD_TYPE_SINGLE_SELECT, CUSTOM_FIELD_TYPE_MULTI_SELECT].includes(field.type) &&
      field.externalSelect != null
    );
  }

  toggleExternalSelect(field: any) {
    if (field.externalSelect != null) {
      field.externalSelect = null;
    } else {
      field.externalSelect = '';
    }
  }

  showExternalSelectFunctions(field: any) {
    this.searchablePopoverService.externalSelectFunctions({
      event,
      projectId: this.projectId,
      callback: (id: string) => {
        field.externalSelect = id;
      },
    });
  }

  showRightValueInput(condition: any) {
    return condition.operator !== 'isNotNull' && condition.operator !== 'isNull';
  }

  async changeDateField(condition: any, property: 'rightValue' | 'leftValue') {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        selected: condition[property] ? new Date(condition[property]) : new Date(),
        title: 'Select date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        // Store date as YYYY-MM-DD string
        condition[property] = moment(result).format('YYYY-MM-DD');
      }
    });
  }

  getExternalSelectFunctionName(field: any) {
    if (!field.externalSelect) {
      return '';
    }

    return parseExternalSelectFunctionName(field.externalSelect, this.projectId).functionName;
  }
}
